import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import dayjs, { Dayjs } from 'dayjs';

import i18n from '../../../assets/languages/configs';
import CustomCalendar from '../../../components/CustomCalendar/CustomCalendar';
import Header from '../../../components/header/Header';
import CenterLoading from '../../../components/CenterLoading/CenterLoading';
import useReservationTicket from '../../../hooks/useReservationTicket';
import { useReservationFlow } from '../../../hooks/useReservationFlow';
import ReservationTicketList from '../ReservationTicketInfoScreen/ReservationTicketList/ReservationTicketList';
import { LocalStorageManager } from '../../../utils/LocalStorageManager/LocalStorageManager';
import { localStorageKeys, STOCK_SALE_STATUS } from '../../../utils/Constants';
import { getValueByLanguage } from '../../../utils';

import styles from './styles.module.css';
import { SelectNumber, Stocks, TicketInfo, MultilingualType } from 'types';

const ReservationCalendarScreen = () => {
    const { currentIndex, next, back, reservationCombo, isEdit, selectNumber, ticketInfo, localReservation } =
        useReservationFlow();
    const reservationTicket = reservationCombo[currentIndex];
    const reserveOn = reservationTicket?.reservationInfo?.reserveOn;
    const [selectedTime, setSelectedTime] = useState<Dayjs>(() => {
        return reserveOn && !dayjs(reserveOn).isBefore(dayjs(), 'date') ? dayjs(reserveOn) : dayjs();
    });

    const navigate = useNavigate();
    const { t } = useTranslation();
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    const {
        isLoading: isFetchingReservationTickets,
        reservationTicketList: stocks,
        reservationCalendar,
        holidays,
        fetchReservationTickets,
        fetchReservationCalendar,
        fetchHolidays,
    } = useReservationTicket();
    const { language } = i18n;
    const l = useCallback((text?: MultilingualType | null) => getValueByLanguage(text, language), [language]);

    const isDateClicked = useRef<boolean>();

    const localStorageManager = useMemo(() => new LocalStorageManager('ReservationTicketInfoScreen'), []);

    const { month, year } = useMemo(() => {
        return { month: selectedTime.month(), year: selectedTime.year() };
    }, [selectedTime]);

    const { startTargetReserveOn, endTargetReserveOn } = useMemo(() => {
        if (currentIndex <= 0) {
            return {
                startTargetReserveOn: null,
                endTargetReserveOn: null,
            };
        }
        const storageReservation = JSON.parse(localStorage.getItem(localStorageKeys.RESERVATION_INFO_DATA) ?? '{}');
        const selectedReservationCombo: TicketInfo[] = storageReservation.reservationCombo.slice(0, currentIndex) ?? [];
        const startTargetReserveOn = selectedReservationCombo.find(
            (r) => r.saleCombinationTicketId === reservationTicket.startTargetId,
        )?.reservationInfo?.reserveOn;
        const endTargetReserveOn = selectedReservationCombo.find(
            (r) => r.saleCombinationTicketId === reservationTicket.endTargetId,
        )?.reservationInfo?.reserveOn;

        return {
            startTargetReserveOn: startTargetReserveOn ?? null,
            endTargetReserveOn: endTargetReserveOn ?? null,
        };
    }, [currentIndex]);

    // 在庫一覧の取得
    useEffect(() => {
        const currentDate = dayjs();

        if (selectedTime !== null && !selectedTime.isBefore(currentDate, 'date')) {
            const handler = setTimeout(() => {
                const requestParams = {
                    saleCombinationTicketId: reservationTicket?.saleCombinationTicketId,
                    year: selectedTime.year(),
                    month: selectedTime.month() + 1,
                    day: selectedTime.date(),
                    startTargetReserveOn: reservationTicket?.startTargetId ? startTargetReserveOn : null,
                    endTargetReserveOn: reservationTicket?.endTargetId ? endTargetReserveOn : null,
                };
                void fetchReservationTickets(requestParams);
            }, 100);

            return () => clearTimeout(handler);
        }
    }, [selectedTime, reservationTicket?.saleCombinationTicketId]);

    // 予約カレンダー取得
    useEffect(() => {
        const requestParams = {
            saleCombinationTicketId: reservationTicket?.saleCombinationTicketId,
            year: year,
            month: month + 1,
            startTargetReserveOn: reservationTicket?.startTargetId ? startTargetReserveOn : null,
            endTargetReserveOn: reservationTicket?.endTargetId ? endTargetReserveOn : null,
        };
        void fetchReservationCalendar(requestParams);
    }, [month, year, reservationTicket?.saleCombinationTicketId, fetchReservationCalendar]);

    useEffect(() => {
        void fetchHolidays();
    }, [fetchHolidays]);

    useEffect(() => {
        if (reserveOn && !dayjs(reserveOn).isBefore(dayjs(), 'date')) {
            setSelectedTime(dayjs(reserveOn));
        }
    }, [currentIndex]);

    const holidaysInCurrentTime = useMemo(() => {
        return holidays.filter((holiday) => dayjs().set('year', year).isSame(holiday.holidayOn, 'year'));
    }, [holidays, year]);

    const isSoldOut = (
        stock: { saleStatus: string; maxReservationCount: number; currentReservationCount: number },
        selectedNumber: SelectNumber,
    ) => {
        const totalSelectNumber = Object.values(selectedNumber)
            .filter((value) => value !== undefined && value !== null)
            .reduce((accumulator, currentValue) => accumulator + currentValue, 0);

        return (
            stock.saleStatus === STOCK_SALE_STATUS.soldOut ||
            (stock.saleStatus === STOCK_SALE_STATUS.onSale &&
                stock.maxReservationCount - stock.currentReservationCount < totalSelectNumber)
        );
    };

    //席の情報を取得する;
    const seatsInfo = useMemo(() => {
        const occupancyFewPercentage = reservationTicket?.occupancyFewPercentage ?? 0;
        const availableDates = new Set();
        const fewDates = new Set();
        const fullDates = new Set();

        //データを集約します
        const initData: Record<string, { currentReservationCount: number; maxReservationCount: number }> = {};
        const aggregatedData = reservationCalendar.reduce(
            (acc, { date, currentReservationCount, maxReservationCount, saleStatus }) => {
                const formattedDate = dayjs(date).format('YYYY-MM-DD');
                const isOnSale = saleStatus === STOCK_SALE_STATUS.onSale;
                const soldOut = isSoldOut({ currentReservationCount, maxReservationCount, saleStatus }, selectNumber);

                acc[formattedDate] ??= {
                    currentReservationCount: 0,
                    maxReservationCount: 0,
                };
                if (isOnSale && !soldOut) {
                    acc[formattedDate].currentReservationCount += currentReservationCount;
                    acc[formattedDate].maxReservationCount += maxReservationCount;
                } else {
                    //他に在庫のある便があるときに、△になる割合の計算にも用いるため
                    acc[formattedDate].currentReservationCount += maxReservationCount;
                    acc[formattedDate].maxReservationCount += maxReservationCount;
                }
                return acc;
            },
            initData,
        );

        //集計データを処理して日付を分類する
        Object.entries(aggregatedData).forEach(([formattedDate, { currentReservationCount, maxReservationCount }]) => {
            const reservationPercentage = ((maxReservationCount - currentReservationCount) / maxReservationCount) * 100;

            if (reservationPercentage > occupancyFewPercentage) {
                availableDates.add(formattedDate);
            } else if (reservationPercentage > 0) {
                fewDates.add(formattedDate);
            } else {
                fullDates.add(formattedDate);
            }
        });

        return {
            availableDates,
            fewDates,
            fullDates,
        };
    }, [reservationCalendar, reservationTicket, selectNumber]);

    useEffect(() => {
        if (isDateClicked.current) {
            const scrollContainer = scrollContainerRef.current;
            scrollContainer?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        isDateClicked.current = false;
    }, [stocks]);

    const handleTimeChange = useCallback((date: Dayjs) => {
        setSelectedTime(date);
    }, []);

    const handleSelectTicket = useCallback(
        (ticket: Stocks[number]) => {
            const reservationInfoData = {
                ...localReservation,
                reservationCombo: reservationCombo?.map((item) =>
                    item.displayPriority === reservationTicket.displayPriority
                        ? {
                              ...item,
                              reservationInfo: {
                                  ...item?.reservationInfo,
                                  ...ticket,
                                  originStop: null,
                                  destStop: null,
                              },
                          }
                        : item,
                ),
            };
            localStorage.setItem(localStorageKeys.RESERVATION_INFO_DATA, JSON.stringify(reservationInfoData));
            next('calendar');
        },
        [localReservation, isEdit, selectNumber, reservationTicket, localStorageManager, navigate],
    );

    const handleDateClick = useCallback(
        async (clickDate: Dayjs) => {
            isDateClicked.current = true;
            if (selectedTime.isSame(clickDate, 'date')) {
                const requestParams = {
                    saleCombinationTicketId: reservationTicket?.saleCombinationTicketId,
                    year: selectedTime.year(),
                    month: selectedTime.month() + 1,
                    day: selectedTime.date(),
                    startTargetReserveOn: reservationTicket?.startTargetId ? startTargetReserveOn : null,
                    endTargetReserveOn: reservationTicket?.endTargetId ? endTargetReserveOn : null,
                };
                await fetchReservationTickets(requestParams);
            }
        },
        [selectedTime, fetchReservationTickets, reservationTicket, startTargetReserveOn, endTargetReserveOn],
    );

    return (
        <>
            <Header
                title={!isEdit ? t('header.buy-ticket') : t('reservation.header')}
                callback={() => back('calendar')}
            />
            <div className="content-container scrollable">
                {isFetchingReservationTickets && <CenterLoading />}
                {reservationCombo.length > 1 && (
                    <div className={styles.ticketName}>{l(reservationTicket?.ticketName)}</div>
                )}
                {/* カレンダー */}
                <div className={styles.calendar}>
                    <CustomCalendar
                        reservationTicket={reservationTicket}
                        seatsInfo={seatsInfo}
                        holidays={holidaysInCurrentTime}
                        selectedTime={selectedTime}
                        onSelectTime={handleTimeChange}
                        onDateClick={handleDateClick}
                    />
                </div>
                {/* 購入できる便 */}
                <div className={styles.advanceBooking} ref={scrollContainerRef}>
                    {/* <p className={styles.advanceBookingLabel}>{t('reservation.can-booked-label')}</p> */}
                    <ReservationTicketList
                        ticketInfo={{ ...reservationTicket, ...ticketInfo }}
                        reservationTicketList={stocks}
                        selectNumber={selectNumber}
                        onSelectTicket={handleSelectTicket}
                        isSoldOut={isSoldOut}
                        isEdit={isEdit}
                    />
                </div>
            </div>
        </>
    );
};

export default ReservationCalendarScreen;
